//  I named this `lists` instead of `groups` because this refers to the module/view
import { GroupFilterParams } from "@/types/FilterParams.interface"
import Group from "@/types/Group.interface"
import Pagination from "@/types/Pagination.interface"
import Person from "@/types/Person.interface"
import { PersonQueryParams } from "@/types/PaginationParams.interface"
import User from "@/types/User.interface"

export interface GroupPersonSeachParams {
    group_id: number
    searchtext: string
}

interface State {
    listsGroups: Group[]
    listsGroupsParams: GroupFilterParams
    listsGroupsDefaultParams: GroupFilterParams
    listsPaginating: boolean
    listsSelectedGroup: Group
    listsSelectedGroupPersons: Person[]
    listsSelectedGroupPersonsCurrentPage: number
    listsSelectedGroupPersonsLastPage: number
    listsSelectedGroupPersonsNextPage: number
    listsSelectedGroupPersonsParams: PersonQueryParams
    listsSelectedPerson: Person
}

const defaults = () => {
    return {
        listsGroups: [],
        listsGroupsParams: {
            display: 'All Groups',
            key: 'filter',
            value: 'all'
        },
        listsGroupsDefaultParams: {
            display: 'All Groups',
            key: 'filter',
            value: 'all'
        },
        listsPaginating: false,
        listsSelectedGroup: null,
        listsSelectedGroupPersons: [],
        listsSelectedGroupPersonsCurrentPage: null,
        listsSelectedGroupPersonsLastPage: null,
        listsSelectedGroupPersonsNextPage: null,
        listsSelectedGroupPersonsParams: { page: 1 },
        listsSelectedPerson: null
    }
}
export default {
    state: defaults(),
    mutations: {
        //  Lists Groups
        listsGroupsParamsSet (state: State, params: GroupFilterParams): void {
            state.listsGroupsParams = params
        },
        listsGroupsPush (state: State, groups: Group[]): void {
            //  Update our list
            state.listsGroups = window.removeDuplicatesById(state.listsGroups, groups)

            //  Get the currently selected group
            const selectedGroup = state.listsSelectedGroup

            //  If the group does NOT exist, we have nothing to do
            if (!selectedGroup) return

            //  Else, find the selected group in the array of updated groups
            const updatedGroup = groups.find((x: Group) => x.id == selectedGroup.id)

            //  If the selected group was NOT updated, we have nothing to do
            if (!updatedGroup) return

            //  Get the group versions
            const updatedVersion = window.getDate(updatedGroup.updated_at).valueOf()
            const selectedVersion = window.getDate(selectedGroup.updated_at).valueOf()

            //  Check if the selected group needs to be updated
            const needsUpdate = updatedVersion > selectedVersion

            //  If we need to update the selected group do it!
            if (needsUpdate) state.listsSelectedGroup = updatedGroup
        },
        listsGroupsRemove(state: State, ids: number[]): void {
            //  Get our current lists of groups
            const groups = state.listsGroups

            //  If no groups, do nothing
            if (!groups) return

            //  Else, filter out our removed ids
            const filtered = groups.filter((x: Group) => !ids.includes(x.id))

            //  Set our groups to the filtered list
            state.listsGroups = filtered

            //  Get the currently selected group
            const selectedGroup = state.listsSelectedGroup

            //  If the group does NOT exist, we have nothing to do
            if (!selectedGroup) return

            //  Else, check if this group was deleted
            const deleted = ids.find((x: number) => x == selectedGroup.id)

            //  If the selected group was NOT found, then do nothing
            if (!deleted) return

            //  Else, set our selected group to null
            state.listsSelectedGroup = null
        },
        listsGroupsSet (state: State, groups: Group[]): void {
            state.listsGroups = groups
        },
        //  Lists Selected Group Persons
        listsSelectedGroupPersonsPush (state: State, persons: Person[]): void {
            //  Update the state
            state.listsSelectedGroupPersons = window.removeDuplicatesById(state.listsSelectedGroupPersons, persons)

            //  Get the currently selected person
            const selectedPerson = state.listsSelectedPerson

            //  If there is no selected person, do nothing
            if (!selectedPerson) return

            //  Else, find the selected person in the array of updated persons
            const updatedPerson = persons.find((x: Person) => x.id == selectedPerson.id)

            //  If the selected person was NOT updated, we have nothing to do
            if (!updatedPerson)

            //  Else, set our selected person to the updated person
            state.listsSelectedPerson = updatedPerson
        },
        listsSelectedGroupPersonsRemove (state: State, ids: number[]): void {
            //  Get our current list of group persons
            const persons = state.listsSelectedGroupPersons

            //  If the persons don't exist, return
            if (!persons) return

            //  Else, filter out the removed ids
            const filtered = persons.filter((x: Person) => !ids.includes(x.id))

            //  Replace our persons with the filtered list
            state.listsSelectedGroupPersons = filtered

            //  Get the currently selected person
            const selectedPerson = state.listsSelectedPerson

            //  If there is no selected person, do nothing
            if (!selectedPerson) return

            //  Else, check if this person is in our list of removed ids
            const deleted = ids.find((x: number) => x == selectedPerson.id)

            //  If the selected person was NOT found, do nothing
            if (!deleted) return

            //  Else, set our selected person to null
            state.listsSelectedPerson = null
        },
        listsSelectedGroupPersonsSet (state: State, persons: Person[]): void {
            //  Set our persons
            state.listsSelectedGroupPersons = persons
        },
        listsSelectedGroupPersonsSync (state: State, updates: Person[]): void {
            //  Get our list of persons
            const persons = state.listsSelectedGroupPersons

            //  If no persons, do nothing
            if (!persons) return

            //  Grab all the ids from our persons
            const ids = persons.map((x: Person) => x.id)

            //  Else, filter out our updates to ONLY contain items that are in the persons list
            const filtered = updates.filter((x: Person) => ids.includes(x.id))

            //  Update our list with the filtered array
            state.listsSelectedGroupPersons = window.removeDuplicatesById(state.listsSelectedGroupPersons, filtered)

            //  Check if we have a selected person
            const selectedPerson = state.listsSelectedPerson

            //  If it doesn't exist, do nothing
            if (!selectedPerson) return

            //  Else, check if our filtered contains this ids
            const updatedPerson = filtered.find((x: Person) => x.id == selectedPerson.id)

            //  If it does NOT contain the id, do nothing
            if (!updatedPerson) return

            //  Else, update our selected person
            state.listsSelectedPerson = updatedPerson
        },
        listsPaginationPush(state: State, page: Pagination) :void {
            //  If this is page one, reset the persons list
            if (page.current_page === 1) state.listsSelectedGroupPersons = []

            state.listsSelectedGroupPersonsCurrentPage = page.current_page
            state.listsSelectedGroupPersonsLastPage = page.last_page
            state.listsSelectedGroupPersonsNextPage = page.current_page + 1
        },
        listsPaginatingSet(state: State, bool: boolean): void {
            state.listsPaginating = bool
        },
        listsSelectedGroupSet(state: State, group: Group): void {
            state.listsSelectedGroup = group
        },
        listsSelectedPersonSet(state: State, person: Person): void {
            state.listsSelectedPerson = person
        },
        listsSelectedGroupPersonsParamsSet(state: State, params: PersonQueryParams): void {
            state.listsSelectedGroupPersonsParams = params
        },
        listsReset (state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        listsSelectedGroupGet({ commit, getters }, groupId: number): Promise<void> {
            //  Grab our global groups dataset
            const groups = getters.groups

            //  Check if our group we are searching for already exists
            const existingGroup = groups.find((x: Group) => x.id === groupId)

            //  If the group was found, return it
            if (existingGroup) return commit('listsSelectedGroupSet', existingGroup)

            //  Else, we have to find this group so assemble our API endpoint
            const url = `${getters.api}/groups/${groupId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our selected group
                commit('listsSelectedGroupSet', response.data)

                //  Push into our global dataset
                commit('groupsPush', [response.data])
            }).catch(() => null)
        },
        listsSelectedGroupPersonsGet({ commit, getters }): Promise<void> {
            //  Our params include pagination params and the group
            const payload = getters.listsSelectedGroupPersonsParams

            //  This is a query group so we need to get the query persons, assemble API endpoint
            const url = `${getters.api}/persons/query`

            //  Start our paginating
            commit('listsPaginatingSet', true)

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Update pagination params (this must occur BEFORE updating the persons list, for reset to work properly)
                commit('listsPaginationPush', response.data)

                //  Update the lists persons list
                commit('listsSelectedGroupPersonsPush', response.data.data)

                //  Stop the 'paginating' tracker
                commit('listsPaginatingSet', false)

                //  Return the pagination data
                return response.data
            }).catch(() => null)
        },
        listsSelectedGroupPersonsSearch({ getters }, params: GroupPersonSeachParams): Promise<Person[]> {
            //  Grab the group id from our params
            const groupId = params.group_id

            //  Grab our search text from the params
            const txt = params.searchtext
            
            //  Assemble the API endpoint
            const url = `${getters.api}/groups/${groupId}/search?searchtext=${txt}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return our search results
                return response.data
            }).catch(() => null)
        },
        listsSelectedGroupPersonsWhereIn({ getters, commit }, ids: Number[]): Promise<Person[]> {
            //  Create our payload
            const payload = { person_ids: ids, sort: 'name_family' }

            //  Assemble the API endpoint
            const url = `${getters.api}/persons/wherein`

            //  Send the request
            return window.axios.post(url, payload).then(response => {

                //  Update the lists persons list
                commit('listsSelectedGroupPersonsSet', response.data)

                //  Return our found persons
                return response.data
            }).catch(() => null)
        },
        listsSelectedPersonGet({ commit, getters }, personId: number): Promise<void> {
            //  Get the global persons list
            const persons = getters.persons

            //  Check for this person in that list
            const existingPerson = persons.find((x: Person) => x.id == personId)

            //  If there IS an existing person, commit that and proceed
            if (existingPerson) return commit('listsSelectedPersonSet', existingPerson)

            //  Else, assembl our API endpoint
            const url = `${getters.api}/persons/${personId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the lists selected person
                commit('listsSelectedPersonSet', response.data)

                //  Push this person into our global dataset
                commit('personsPush', [response.data])

                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        listsGroups (state: State): Group[] {
            return state.listsGroups
        },
        listsPaginating (state: State): boolean {
            return state.listsPaginating
        },
        listsGroupsDefaultParams (state: State): GroupFilterParams {
            return state.listsGroupsDefaultParams
        },
        listsGroupsParams (state: State): GroupFilterParams {
            return state.listsGroupsParams
        },
        listsSelectedGroup (state: State): Group {
            return state.listsSelectedGroup
        },
        listsSelectedGroupPersons (state: State): Person[] {
            return state.listsSelectedGroupPersons
        },
        listsSelectedGroupPersonsCurrentPage (state: State): number {
            return state.listsSelectedGroupPersonsCurrentPage
        },
        listsSelectedGroupPersonsLastPage (state: State): number {
            return state.listsSelectedGroupPersonsLastPage
        },
        listsSelectedGroupPersonsParams (state: State): PersonQueryParams {
            return state.listsSelectedGroupPersonsParams
        },
        listsSelectedPerson (state: State): Person {
            return state.listsSelectedPerson
        }
    }
}
